import dateTime from '@/utils/dateTime'

import {
  Plan,
  Subscription,
  TransformPlansProps,
  TransformPlanProps,
  PlanType,
  Product,
} from './types'

const planTypeTier: Record<string, number> = {
  [PlanType.Starter]: 0,
  [PlanType.Wizard]: 1,
  [PlanType.Dashboard]: 2,
  [PlanType.Seal]: 3,
}

export const getSubscriptionPriceInfo = (subscription: Subscription) => {
  const priceId = subscription.items.data[0].price.id
  const priceAmount = (subscription.items.data[0].price.unit_amount || 0)
  const price = priceAmount !== 0 ? toFormattedUSD(priceAmount / 100) : 'Free'
  const billingInterval = subscription.items.data[0].price.recurring?.interval

  return {
    priceId,
    price,
    billingInterval,
    isFree: priceAmount === 0,
  }
}

export const getCustomerIdFromSubscription = (subscription: Subscription) => {
  if (typeof subscription.customer === 'string') {
    return subscription.customer
  }

  return subscription.customer.id
}

export const getSubscriptionExpiration = (subscription: Subscription) => {
  // In API 2025-03-31.basil+, current_period_end moved to subscription items
  const currentPeriodEnd = subscription.items.data[0].current_period_end

  return dateTime.format(new Date(currentPeriodEnd * 1000), 'MM.dd.yyyy')
}

export const subscriptionHasFailedPayments = (subscription: Subscription) => subscription.status === 'past_due' || subscription.status === 'unpaid'

export const getPlanPriceInfo = (plan: Plan, priceIndex = 0) => {
  const priceObject = plan.prices[priceIndex]
  const priceId = priceObject.id
  const priceAmount = priceObject.unit_amount || 0
  const price = priceAmount !== 0 ? toFormattedUSD(priceAmount / 100) : 'Free'
  const billingInterval = (priceAmount !== 0 ? priceObject.recurring?.interval : '') || ''

  return {
    priceId,
    price,
    billingInterval,
    isFree: priceAmount === 0,
    priceAmount,
  }
}

export const transformPlan = ({
  plan,
  subscription,
}: TransformPlanProps) => {
  const subscribedProduct = subscription?.items.data[0].price.product
  let subscribedProductId
  if (subscribedProduct) {
    subscribedProductId = typeof subscribedProduct === 'string' ? subscribedProduct : subscribedProduct.id
  }
  const planPriceInfo = getPlanPriceInfo(plan)

  return {
    id: plan.id,
    name: plan.name,
    tagline: plan.description,
    ...planPriceInfo,
    features: plan.marketing_features,
    order: plan.metadata.order,
    type: plan.metadata.type,
    isActive: subscribedProductId === plan.id,
    scheduledForDowngrade: subscription?.upcomingPlanChangePhase?.items?.[0]?.price === planPriceInfo.priceId,
    downgradeHelperText: subscription?.upcomingPlanChangePhase?.items?.[0]?.price === planPriceInfo.priceId ? ` Scheduled for change on ${dateTime.format(new Date((subscription?.upcomingPlanChangePhase?.start_date || 0) * 1000), 'MM.dd.yyyy')}` : '',
  }
}

export const transformPlans = ({
  plans,
  subscription,
}: TransformPlansProps) => plans.map(plan => transformPlan({
  plan,
  subscription,
})).sort((a, b) => a.order.localeCompare(b.order))

export const sortPlansByOrder = (plans: Plan[]) => plans.sort((a, b) => a.metadata.order.localeCompare(b.metadata.order))

export const getHighestTieredProduct = (subscription: Subscription) => {
  const products = subscription.items.data.map(item => item.price.product as Product)

  const highestTieredProduct = products.reduce((max, current) => {
    const currentTier = parseInt(current.metadata?.order || '0', 10)
    const maxTier = parseInt(max.metadata?.order || '0', 10)

    if (currentTier > maxTier) {
      return current
    }

    return max
  }, products[0])

  return highestTieredProduct
}

export const getPlanType = (subscription: Subscription): string | undefined => getHighestTieredProduct(subscription)?.metadata?.type

export const getPlanName = (subscription: Subscription) => {
  const planType = getHighestTieredProduct(subscription)?.metadata?.type

  switch (planType) {
    case PlanType.Starter:
      return 'Get Started'
    case PlanType.Wizard:
      return 'Wizard'
    case PlanType.Dashboard:
      return 'Dashboard'
    case PlanType.Seal:
      return 'Common Sense Privacy Seal'
    default:
      return 'Unknown'
  }
}

export const isAtLeastStarterPlan = (planType: string) => (
  planType === PlanType.Starter
  || planType === PlanType.Wizard
  || planType === PlanType.Dashboard
  || planType === PlanType.Seal
)

export const isAtLeastWizardPlan = (planType: string) => (
  planType === PlanType.Wizard
  || planType === PlanType.Dashboard
  || planType === PlanType.Seal
)

export const isAtLeastDashboardPlan = (planType: string) => (
  planType === PlanType.Dashboard
  || planType === PlanType.Seal
)

export const isAtLeastSealPlan = (planType: string) => planType === PlanType.Seal

export const isStarterPlan = (planType: string) => planType === PlanType.Starter

export const isWizardPlan = (planType: string) => planType === PlanType.Wizard

export const isDashboardPlan = (planType: string) => planType === PlanType.Dashboard

export const isSealPlan = (planType: string) => planType === PlanType.Seal

export const isHigherTierPlan = (planTypeA: string, planTypeB: string) => planTypeTier[planTypeA] > planTypeTier[planTypeB]

// hardcoding to USD for now
export const toFormattedUSD = (amount: number): string => new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
}).format(amount)
